import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@natu/ui/Accordion';
import { DynamicIcon } from '@natu/ui/DynamicIcon';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@natu/ui/Tabs';
import { cn } from '@natu/utils/cn';
import { isArrayWithLength } from '@natu/utils/isArrayWithLength';

import {
  type BlokItem,
  DynamicRender,
  resolveStoryblokStyles,
  sbEditable,
  type SBProps,
  type Spacing,
} from '../../../../utils';

interface ItemProp extends BlokItem<'supportItem'> {
  title?: string;
}

interface SBSupportSectionProps {
  title?: string;
  subtitle?: string;
  items: ItemProp[];
  mtMobile?: Spacing;
  mtTablet?: Spacing;
  mtDesktop?: Spacing;
  mbMobile?: Spacing;
  mbTablet?: Spacing;
  mbDesktop?: Spacing;
  sendEvent?: boolean;
  event_solution_name?: string;
  closeAsDefault?: boolean;
}

const getSupportItemData = ({ items }: { items: ItemProp[] }) =>
  items.filter(item => item.component === 'supportItem');

export const SBSupportSection = ({ blok }: SBProps<SBSupportSectionProps>) => {
  const {
    items,
    subtitle,
    title: sectionTitle,
    mbMobile,
    mbTablet,
    mbDesktop,
    mtMobile,
    mtTablet,
    mtDesktop,
    sendEvent,
    event_solution_name,
    closeAsDefault = false,
  } = blok;

  const className = resolveStoryblokStyles({
    mt: mtMobile,
    mtTablet,
    mtDesktop,
    mb: mbMobile,
    mbTablet,
    mbDesktop,
  });

  const data = getSupportItemData({ items });

  if (!isArrayWithLength(data)) return null;

  return (
    <div className={cn(className, 'flex flex-col gap-y-10')} {...sbEditable(blok)}>
      <div className="flex flex-col gap-y-3">
        {sectionTitle && (
          <p className="text-primary-600 text-sm font-semibold xl:text-base">{sectionTitle}</p>
        )}
        {subtitle && (
          <h2 className="text-display-sm-28 xl:text-display-md-34 font-semibold">{subtitle}</h2>
        )}
      </div>

      <div className="lg:hidden">
        <Accordion
          type="single"
          collapsible
          className="flex flex-col gap-y-4"
          defaultValue={closeAsDefault ? undefined : data[0]._uid}
        >
          {data.map(item => (
            <AccordionItem key={item._uid} value={item._uid} className="group">
              <AccordionTrigger
                className="data-[state=open]:bg-primary-600 mb-4 items-center rounded-lg bg-gray-100 px-4 py-3.5 text-left text-lg font-semibold text-gray-600 data-[state=open]:text-white group-last:data-[state=closed]:mb-0"
                event={sendEvent ? 'E_solutions_expand' : undefined}
                eventProps={{ event_solution_name, text: item.title }}
              >
                {item.title}
                <DynamicIcon icon="chevron" />
              </AccordionTrigger>
              <AccordionContent>
                <DynamicRender data={item} />
              </AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
      </div>

      <Tabs defaultValue={data[0]._uid} className="hidden gap-x-16 lg:flex xl:gap-x-32">
        <TabsList className="flex w-full max-w-[489px] flex-col gap-y-3">
          {data.map(item => (
            <h3 key={item._uid} className="block w-full">
              <TabsTrigger
                value={item._uid}
                className="data-[state=active]:bg-primary-600 w-full items-center justify-start gap-x-6 rounded-lg border-0 px-4 py-3.5 text-lg font-semibold data-[state=active]:border-0 data-[state=active]:text-white [&[data-state=inactive]>svg]:hidden"
                event={sendEvent ? 'E_solutions_expand' : undefined}
                eventProps={{ event_solution_name, text: item.title }}
              >
                {item.title}
                <DynamicIcon icon="arrowRight" className="text-white" />
              </TabsTrigger>
            </h3>
          ))}
        </TabsList>
        <div className="w-full lg:-mt-[6.875rem]">
          {data.map(item => (
            <TabsContent className="mt-0" key={item._uid} value={item._uid}>
              <DynamicRender data={item} />
            </TabsContent>
          ))}
        </div>
      </Tabs>
    </div>
  );
};
